import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Wand2, Loader2, Trash2, X, Download, Copy, Palette } from "lucide-react";

const CATEGORIES = ["Ready-to-Wear", "Eveningwear", "Couture", "Lifestyle & Accessories"];

export default function RunwayPage({ seed, onSeedConsumed }) {
  const [designs, setDesigns] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [collection, setCollection] = useState("Season 01");
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [varLoading, setVarLoading] = useState(null);
  const [seedInfo, setSeedInfo] = useState(null);

  const load = () => api.getDesigns().then(setDesigns).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!seed) return;
    const parts = [];
    if (seed.mood) parts.push(`A ${seed.mood.toLowerCase()} concept look`);
    if (seed.tags?.length) parts.push(seed.tags.slice(0, 4).join(", "));
    if (seed.palette?.length) parts.push(`in a palette of ${seed.palette.join(", ")}`);
    if (seed.direction) parts.push(seed.direction);
    setPrompt(parts.join(". "));
    setSeedInfo({ palette: seed.palette || [], mood: seed.mood });
    toast.success("Palette loaded into Runway", { description: "Tweak the prompt and generate." });
    onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const generate = async () => {
    if (!prompt.trim() || loading) return;
    setLoading(true);
    try {
      const res = await api.generateDesign(prompt.trim(), category, collection.trim() || "Season 01");
      setDesigns((d) => [res, ...d]);
      setPrompt("");
      toast.success("Look generated", { description: "Added to the runway gallery" });
    } catch (e) {
      toast.error("Generation failed", { description: e?.response?.data?.detail || "Try rephrasing." });
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    await api.deleteDesign(id);
    setDesigns((d) => d.filter((x) => x.id !== id));
    setLightbox(null);
    toast.success("Look removed");
  };

  const download = (d) => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${d.image_base64}`;
    a.download = `${(d.title || "look").replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const makeVariations = async (d) => {
    setVarLoading(d.id);
    try {
      const created = await api.generateVariations(d.id);
      setDesigns((list) => [...created, ...list]);
      toast.success(`${created.length} variations generated`, { description: "Added to the gallery" });
    } catch (e) {
      toast.error("Variations failed", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setVarLoading(null);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 min-h-screen">
      <header className="pb-6 border-b border-white/10">
        <div className="overline-label">Couture Runway Studio</div>
        <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight mt-1">Concept Look Generator</h2>
        <p className="text-neutral-400 mt-2 max-w-2xl">
          Translate a vision into editorial imagery. Describe the look, pick a category and a
          collection drop — ATELIER renders it on the runway.
        </p>
      </header>

      <div className="card-lux rounded-xl p-6 sm:p-8 mt-8 space-y-4">
        <textarea
          data-testid="design-prompt-input"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={3}
          placeholder="e.g. A liquid-silver draped gown with architectural shoulders, raw hem, under cold studio light"
          className="w-full resize-none bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-4 py-3 text-neutral-100 placeholder:text-neutral-600 outline-none"
        />
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            data-testid="design-category-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-4 py-3 text-neutral-200 outline-none flex-1"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            data-testid="design-collection-input"
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            placeholder="Collection / drop"
            className="bg-[#141418] border border-white/10 focus:border-amber-500/50 rounded-lg px-4 py-3 text-neutral-200 outline-none flex-1"
          />
          <button
            data-testid="generate-design-btn"
            onClick={generate}
            disabled={loading || !prompt.trim()}
            className="btn-gold rounded-lg px-6 py-3 font-medium flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Wand2 size={18} />}
            {loading ? "Rendering…" : "Generate Look"}
          </button>
        </div>
      </div>

      {seedInfo && (seedInfo.palette?.length > 0 || seedInfo.mood) && (
        <div className="card-lux rounded-xl p-4 mt-4 flex items-center gap-3 flex-wrap" data-testid="runway-seed-banner">
          <Palette size={16} className="text-amber-400" />
          <span className="text-sm text-neutral-300">From your vault{seedInfo.mood ? `: ${seedInfo.mood}` : ""}</span>
          <div className="flex gap-1.5">
            {seedInfo.palette.map((c, i) => (
              <span key={i} className="w-6 h-6 rounded border border-white/10" style={{ background: c }} title={c} />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-8">
        {loading && (
          <div className="skeleton rounded-xl aspect-[3/4]" />
        )}
        {designs.map((d) => (
          <div key={d.id} className="card-lux rounded-xl overflow-hidden group" data-testid={`runway-card-${d.id}`}>
            <div className="relative cursor-pointer" onClick={() => setLightbox(d)}>
              <img
                src={`data:image/png;base64,${d.image_base64}`}
                alt={d.title}
                className="w-full aspect-[3/4] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="p-4">
              <div className="overline-label text-[9px]">{d.category}</div>
              <p className="text-sm text-neutral-200 mt-1 line-clamp-2">{d.prompt}</p>
              <p className="text-[11px] font-mono text-neutral-500 mt-2">{d.collection}</p>
              <button
                data-testid={`variations-btn-${d.id}`}
                onClick={(e) => { e.stopPropagation(); makeVariations(d); }}
                disabled={varLoading === d.id}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 hover:border-amber-500/40 text-neutral-300 hover:text-amber-300 transition-colors px-3 py-2 text-xs"
              >
                {varLoading === d.id ? <Loader2 className="animate-spin" size={14} /> : <Copy size={14} />}
                {varLoading === d.id ? "Generating…" : "Give me 3 variations"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {designs.length === 0 && !loading && (
        <p className="text-neutral-500 text-center mt-16">No looks yet — describe your first concept above.</p>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="runway-lightbox"
        >
          <div className="max-w-5xl w-full flex flex-col lg:flex-row gap-6 items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={`data:image/png;base64,${lightbox.image_base64}`}
              alt={lightbox.title}
              className="max-h-[80vh] rounded-xl border border-white/10"
            />
            <div className="lg:w-80 flex-shrink-0">
              <div className="overline-label">{lightbox.category}</div>
              <h3 className="font-serif text-2xl mt-2">{lightbox.collection}</h3>
              <p className="text-neutral-300 mt-3 leading-relaxed">{lightbox.prompt}</p>
              <div className="flex gap-3 mt-6 flex-wrap">
                <button onClick={() => download(lightbox)} className="btn-gold rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm" data-testid="download-look-btn">
                  <Download size={16} /> Download
                </button>
                <button
                  onClick={() => makeVariations(lightbox)}
                  disabled={varLoading === lightbox.id}
                  className="rounded-lg px-4 py-2.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500 hover:text-black transition-colors flex items-center gap-2 text-sm"
                  data-testid="lightbox-variations-btn"
                >
                  {varLoading === lightbox.id ? <Loader2 className="animate-spin" size={16} /> : <Copy size={16} />}
                  3 Variations
                </button>
                <button onClick={() => remove(lightbox.id)} className="rounded-lg px-4 py-2.5 border border-white/10 text-neutral-300 hover:text-red-400 hover:border-red-500/40 flex items-center gap-2 text-sm">
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>
          </div>
          <button className="absolute top-6 right-6 text-neutral-300 hover:text-white" onClick={() => setLightbox(null)}>
            <X size={26} />
          </button>
        </div>
      )}
    </div>
  );
}
